import { Env } from '@env';
import { useEffect } from 'react';

import { useOrganization } from '@/api/organizations/use-organization';

import { getOrganizationSlug } from './parse-subdomain';

export type OrganizationStatus =
  | 'none' // no subdomain -> normal eon site
  | 'loading'
  | 'not-found' // subdomain present but no matching organization
  | 'ready';

/**
 * EV Fleet Management Company. The subscription experience (weekly-only,
 * unlimited mileage, hidden mileage UI, etc.) is currently gated to this single
 * org while the feature set is in flux. When this changes, update the one line
 * that derives `isEVFMC` below.
 */
export const EVFMC_ORG_ID = 1;

/**
 * Resolves the white-label organization for the current subdomain.
 *
 * Safe to call from any component: the slug is derived from the URL and the
 * request is deduped + cached by React Query, so there is a single source of
 * truth. On a full page refresh the slug is re-derived and the query re-runs.
 */
export function useCurrentOrganization() {
  const slug = getOrganizationSlug();

  const { data, isLoading, isError } = useOrganization({
    variables: { slug: slug ?? '' },
    enabled: !!slug,
  });

  const status: OrganizationStatus = !slug
    ? 'none'
    : isLoading
    ? 'loading'
    : isError
    ? 'not-found'
    : 'ready';

  const organization = data ?? null;

  return {
    slug,
    organization,
    isEVFMC: organization?.id === EVFMC_ORG_ID,
    isLoading,
    isError,
    status,
  } as const;
}

/**
 * Root-level bootstrap. Mount once near the top of the tree to handle the
 * "unknown subdomain" case by sending the visitor back to the main site.
 */
export function useOrganizationBootstrap() {
  const { isError } = useCurrentOrganization();

  useEffect(() => {
    if (isError) {
      window.location.href = `${window.location.protocol}//${Env.BASE_URL}`;
    }
  }, [isError]);
}
